import { createMachine } from '../state-machine';
import {
  type BaseState,
  type BaseEvent,
  type BaseEventLookup,
  type BaseContext,
  type StateConfig,
  type StateMachineConfig,
  type Transition,
  type StateTransitionConfig,
  type StatePassThroughConfig,
  type StateCommand,
  type Effects,
  type StateGuard,
} from '../state-machine-types';

import {
  type Builder,
  type StateBuilder,
  type TransientStateBuilder,
  type EffectNameToStateEffect,
  type TransitionBuilder,
  type EffectName,
  type EffectLookup,
} from './state-machine-builder-types';

const effectNameMap: EffectNameToStateEffect = {
  enter: 'onEnter',
  're-enter': 'onReEnter',
  exit: 'onExit',
  execute: 'onExecute',
};

/**
 * Use this builder to create state machine configuration with ease.
 * @returns A Builder API for incrementally building
 */
export const builder = <
  S extends BaseState,
  E extends BaseEvent,
  L extends BaseEventLookup<E['type']>,
  C extends BaseContext
>(): Builder<S, E, L, C> => {
  type StateBuilderAPI = StateBuilder<S, E, L, C>;
  type TransientStateBuilderAPI = TransientStateBuilder<S, E, L, C>;
  type StateTree = { states?: Partial<Record<S, StateConfig<S, E, L, C>>> };

  const config = {} as StateMachineConfig<S, E, L, C>;
  config.states = {};

  const initial = (state: S) => {
    config.initial = state;
    return { context };
  };

  const context = (context: C) => {
    config.context = context;
    return { state, transientState, build, when };
  };

  const state = (name: S, doBuild: (builder: StateBuilderAPI) => StateBuilderAPI) => {
    const builder = stateBuilder(name, config);
    doBuild(builder).build();
    return { state, transientState, build, when };
  };

  const transientState = (
    name: S,
    doBuild: (builder: TransientStateBuilderAPI) => TransientStateBuilderAPI
  ) => {
    const builder = statePassthroughBuilder(name, config);
    doBuild(builder).build();
    return { state, transientState, build, when };
  };

  const when = <T extends E['type']>(
    on: T,
    doBuild: (
      builder: TransitionBuilder<T, S, E, L, C>
    ) => Transition<S, C, L, T> | Transition<S, C, L, T>[]
  ) => {
    const builder = transitionBuilder<T>();
    const newTransitions = doBuild(builder);

    if (!config.globalTransitions) {
      config.globalTransitions = {};
    }

    const transitionsForEvent = (config.globalTransitions[on] ??= []);

    if (Array.isArray(newTransitions)) {
      transitionsForEvent.push(...newTransitions);
    } else {
      transitionsForEvent.push(newTransitions);
    }

    return { state, transientState, build, when };
  };

  const stateBuilder = (name: S, config: StateTree): StateBuilderAPI => {
    type StateTransition = StateTransitionConfig<S, E, L, C>;
    const stateConfig = {} as StateTransition;

    // State tree defaults.
    if (!config.states) {
      config.states = {};
    }

    // State defaults.
    if (!stateConfig.effects) {
      stateConfig.effects = {};
    }
    if (!stateConfig.transitions) {
      stateConfig.transitions = {};
    }

    if (!stateConfig.guards) {
      stateConfig.guards = [];
    }

    const when = <T extends E['type']>(
      on: T,
      build: (
        builder: TransitionBuilder<T, S, E, L, C>
      ) => Transition<S, C, L, T> | Transition<S, C, L, T>[]
    ) => {
      if (!stateConfig.transitions[on]) {
        stateConfig.transitions[on] = [];
      }
      const builder = transitionBuilder<T>();
      const transition = build(builder);
      if (Array.isArray(transition)) {
        stateConfig.transitions[on]?.push(...transition);
      } else {
        stateConfig.transitions[on]?.push(transition);
      }
      return stateApi;
    };

    const trap = () => {
      stateConfig.trap = true;
      return stateApi;
    };

    const on = <T extends EffectName>(name: T, callback: EffectLookup<T, C, E>) => {
      effectBuilder(effectNameMap[name], stateConfig, callback);
      return stateApi;
    };

    const childState = (
      name: S,
      build: (builder: StateBuilderAPI) => StateBuilderAPI
    ) => {
      const builder = stateBuilder(name, stateConfig);
      build(builder).build();
      return stateApi;
    };

    const transientChildState = (
      name: S,
      doBuild: (builder: TransientStateBuilderAPI) => TransientStateBuilderAPI
    ) => {
      const builder = statePassthroughBuilder(name, stateConfig);
      doBuild(builder).build();
      return stateApi;
    };

    const build = () => {
      if (config.states) {
        config.states[name] = stateConfig;
      }
      return stateConfig;
    };
    const guard = (guard: StateGuard) => {
      stateConfig.guards?.push(guard);
      return stateApi;
    };

    const stateApi: StateBuilderAPI = {
      when,
      on,
      build,
      state: childState,
      transientState: transientChildState,
      trap,
      guard,
    };

    return stateApi;
  };

  const statePassthroughBuilder = (
    name: S,
    config: StateTree
  ): TransientStateBuilderAPI => {
    type StateTransition = StatePassThroughConfig<S, E, L, C>;
    const stateConfig = {} as StateTransition;

    // State tree defaults.
    if (!config.states) {
      config.states = {};
    }

    // State defaults.
    if (!stateConfig.effects) {
      stateConfig.effects = {};
    }
    if (!stateConfig.passthroughs) {
      stateConfig.passthroughs = [];
    }

    const when = (
      build: (
        builder: TransitionBuilder<E['type'], S, E, L, C>
      ) => Transition<S, C, L, E['type']>
    ) => {
      const builder = transitionBuilder<E['type']>();
      stateConfig.passthroughs.push(build(builder));
      return statePassthroughApi;
    };

    const on = <T extends EffectName>(name: T, callback: EffectLookup<T, C, E>) => {
      effectBuilder(effectNameMap[name], stateConfig, callback);
      return statePassthroughApi;
    };

    const build = () => {
      if (config.states) {
        config.states[name] = stateConfig;
      }
      return stateConfig;
    };

    const statePassthroughApi: TransientStateBuilderAPI = {
      when,
      on,
      build,
    };
    return statePassthroughApi;
  };

  const transitionBuilder = <T extends E['type']>(): TransitionBuilder<T, S, E, L, C> => {
    const transitionConfig = {} as Transition<S, C, L, T>;

    let transitionLabel = 'unknown';
    const label = (label: string) => {
      transitionLabel = label;
      return { if: guard };
    };

    const guard = (...guards: Transition<S, C, L, T>['guard']) => {
      transitionConfig.guard = guards;
      return { context };
    };

    const context = (...context: Transition<S, C, L, T>['context']) => {
      transitionConfig.context = context;
      return { to };
    };

    const to = (state: S | StateCommand) => {
      transitionConfig.state = state;
      return { ...transitionConfig, label: transitionLabel };
    };

    const transitionApi: TransitionBuilder<T, S, E, L, C> = {
      label: label,
      if: guard,
    };
    return transitionApi;
  };

  const effectBuilder = <T extends keyof StateTransitionConfig<S, E, L, C>['effects']>(
    name: T,
    config: StateConfig<S, E, L, C>,
    callback: Effects<C, E>[T]
  ) => {
    config.effects[name] = callback;
  };

  const build = () => {
    return createMachine(config);
  };

  return { initial };
};
